class MobileNavigation extends HTMLElement {
  constructor() {
    super();

    this._visibility = false;

    this.toggleElement = document.querySelector('.mobile-menu-toggle');
    this.toggleListener = this.toggleMenu.bind(this);

    this.overlayEl = document.querySelector('#js-custom-overlay');
    this.overlayListener = () => this.visibility = false;

    // Store bound transition end handler
    this.mainTransitionEndListener = this.handleMainTransitionEnd.bind(this);

    // Event delegation handlers
    this.handleClick = this.handleClick.bind(this);

    // Sub menu handling
    this.subTriggers = this.querySelectorAll('[data-sub-menu-trigger]');
    this.backButtons = this.querySelectorAll('.mobile-navigation__child-back');
    this.mainNavigation = this;

    // Header reference
    this.header = document.querySelector('.cc-header');
  }

  /**
   * Set a css variable to indicate where the nav ends on the page
   */
  setHeaderEnd() {
    if (!this.header) return;

    const headerEnd = Number(this.header.getBoundingClientRect().top + this.header.clientHeight);
    document.documentElement.style.setProperty('--header-end', `${headerEnd.toFixed(1)}px`);
    document.documentElement.style.setProperty(
      '--header-end-padded',
      `${(headerEnd + (window.matchMedia(theme.mediaQueries.md).matches ? 56 : 20)).toFixed(1)}px`
    );
  }

  /**
   * Handle click events through delegation
   * @param {MouseEvent} event
   */
  handleClick(event) {
    // Handle submenu triggers
    const subMenuTrigger = event.target.closest('[data-sub-menu-trigger]');
    if (subMenuTrigger) {
      const handle = subMenuTrigger.dataset.subMenuTrigger;
      this.openSubMenu(handle);
      return;
    }

    // Handle back buttons (both child and grandchild)
    const backButton = event.target.closest('.mobile-navigation__child-back, .mobile-navigation__grandchild-back');
    if (backButton) {
      const menu = backButton.closest('.mobile-navigation__child, .mobile-navigation__grandchild');
      const handle = menu.dataset.subMenu;
      this.closeSubMenu(handle);
      return;
    }
  }

  connectedCallback() {
    if (!this.toggleElement) {
      console.warn('Mobile menu toggle element not found. Make sure there is an element with class "mobile-menu-toggle" in the document.');
      return;
    }

    this.toggleElement.addEventListener('click', this.toggleListener);
    this.mainNavigation.addEventListener('transitionend', this.mainTransitionEndListener);
    this.addEventListener('click', this.handleClick);

    // Initialize brands navigation if it exists
    this.initializeBrandsNavigation();

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:section:select', (event) => {
          this.visibility = true;
        });
      }
    }
  }

  disconnectedCallback() {
    this.toggleElement.removeEventListener('click', this.toggleListener);
    if (this._visibility) {
      this.overlayEl.removeEventListener('click', this.overlayListener);
    }
    this.mainNavigation.removeEventListener('transitionend', this.mainTransitionEndListener);
    this.removeEventListener('click', this.handleClick);

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      if (section) {
        section.removeEventListener('shopify:section:select', (event) => {
          this.visibility = true;
        });
      }
    }
  }

  /**
   * Handle main menu transition end
   * @param {TransitionEvent} event
   */
  handleMainTransitionEnd(event) {
    if (!this._visibility) {
      this.mainNavigation.classList.remove('overflow-hidden');
    }
  }

  /**
   * Opens a sub menu by its handle
   * @param {string} handle
   */
  openSubMenu(handle) {
    const subMenu = this.querySelector(`[data-sub-menu="${handle}"]`);

    if (subMenu) {
      const trigger = this.querySelector(`[data-sub-menu-trigger="${handle}"]`);
      const currentMenu = trigger.closest('.mobile-navigation__child') || this.mainNavigation;
      const isGrandchildPanel = subMenu.classList.contains('mobile-navigation__grandchild');

      // Grandchild panels are siblings of child panels (not inside the scrolling child), so
      // resetting child scroll was only needed for the old nested layout and caused a visible jump.
      if (!isGrandchildPanel) {
        currentMenu.dataset.savedScrollTop = currentMenu.scrollTop;
        currentMenu.classList.add('overflow-hidden');
        currentMenu.scrollTop = 0;
      }

      subMenu.classList.add('is-open');
      this.setHeaderEnd();
    }
  }

  /**
   * Closes a sub menu by its handle
   * @param {string} handle
   */
  closeSubMenu(handle) {
    const subMenu = this.querySelector(`[data-sub-menu="${handle}"]`);
    if (subMenu) {
      const trigger = this.querySelector(`[data-sub-menu-trigger="${handle}"]`);
      const currentMenu = trigger.closest('.mobile-navigation__child') || this.mainNavigation;
      const isGrandchildPanel = subMenu.classList.contains('mobile-navigation__grandchild');

      // Keep overflow-hidden during the transition so the parent's scroll
      // position doesn't shift the absolutely-positioned subMenu mid-animation.
      // Restore everything once the panel has fully slid out.
      const handleTransitionEnd = (event) => {
        if (event.propertyName !== 'transform') return;

        currentMenu.classList.remove('overflow-hidden');

        if (!isGrandchildPanel && currentMenu.dataset.savedScrollTop !== undefined) {
          currentMenu.scrollTop = parseInt(currentMenu.dataset.savedScrollTop, 10);
          delete currentMenu.dataset.savedScrollTop;
        }

        if (handle === 'brands') {
          const brandsContainer = subMenu.querySelector('.mobile-navigation__brands');
          if (brandsContainer) brandsContainer.scrollTop = 0;
        } else {
          subMenu.scrollTop = 0;
        }

        subMenu.removeEventListener('transitionend', handleTransitionEnd);
      };

      subMenu.addEventListener('transitionend', handleTransitionEnd);
      subMenu.classList.remove('is-open');
      this.setHeaderEnd();
    }
  }

  /**
   * Closes all sub menus and resets all scroll positions immediately.
   * Called when the whole nav closes so no transition animations are needed.
   */
  closeAllSubMenus() {
    this.querySelectorAll('.mobile-navigation__child, .mobile-navigation__grandchild').forEach(menu => {
      menu.classList.remove('is-open');
      menu.scrollTop = 0;
      delete menu.dataset.savedScrollTop;

      const brandsContainer = menu.querySelector('.mobile-navigation__brands');
      if (brandsContainer) brandsContainer.scrollTop = 0;
    });

    this.querySelectorAll('.overflow-hidden').forEach(menu => {
      menu.classList.remove('overflow-hidden');
    });

    const innerNav = this.querySelector('.mobile-navigation');
    if (innerNav) innerNav.scrollTop = 0;
    this.setHeaderEnd();
  }

  /**
   * Toggles the visibility of the mobile menu.
   */
  toggleMenu() {
    this.visibility = !this.visibility;
  }

  /**
   * Sets the visibility of the mobile menu.
   * @param {boolean} value
   */
  set visibility(value) {
    this._visibility = value;
    this.classList.toggle('is-open', value);
    this.toggleElement.classList.toggle('is-open', value);
    this.overlayEl.classList.toggle('tw-hidden', !value);
    this.overlayEl.style.opacity = value ? 1 : 0;
    document.body.classList.toggle('site-overflowed', value);

    // Add or remove overlay click listener based on visibility
    if (value) {
      this.overlayEl.addEventListener('click', this.overlayListener);
    } else {
      this.overlayEl.removeEventListener('click', this.overlayListener);
      this.closeAllSubMenus();
    }

    this.setHeaderEnd();
  }

  /**
   * Gets the visibility of the mobile menu.
   * @returns {boolean}
   */
  get visibility() {
    return this._visibility;
  }

  /**
   * Close the mobile navigation
   */
  close() {
    this.visibility = false;
  }

  /**
   * Initialize the brands navigation UI
   */
  initializeBrandsNavigation() {
    const brandsMenu = this.querySelector('[data-brands]');
    if (!brandsMenu) return;

    const brandsDataElement = brandsMenu.querySelector('#brandsData');
    if (!brandsDataElement) return;

    const brandsData = JSON.parse(brandsDataElement.textContent);

    const alphabetContainer = brandsMenu.querySelector('.mobile-navigation__brands-alphabet');
    const listContainer = brandsMenu.querySelector('.mobile-navigation__brands-list');

    // Create alphabet array (no #; number-starting brands are grouped first)
    const alphabet = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];

    // Get first letters of all brands
    const brandFirstLetters = new Set(brandsData.map(brand => {
      const firstChar = brand.title.charAt(0).toUpperCase();
      return firstChar.match(/[A-Z]/) ? firstChar : '#';
    }));

    // Create alphabet UI
    const alphabetHTML = alphabet.map(letter => {
      const hasItems = brandFirstLetters.has(letter);
      return `<button class="mobile-navigation__brands-letter${hasItems ? '' : ' is-disabled'}" data-letter="${letter}">${letter}</button>`;
    }).join('');
    alphabetContainer.innerHTML = alphabetHTML;

    // Group brands by letter
    const brandsByLetter = brandsData.reduce((acc, brand) => {
      const firstChar = brand.title.charAt(0).toUpperCase();
      const letter = firstChar.match(/[A-Z]/) ? firstChar : '#';
      if (!acc[letter]) acc[letter] = [];
      acc[letter].push(brand);
      return acc;
    }, {});

    // Sort brands within each letter
    Object.values(brandsByLetter).forEach(brands => {
      brands.sort((a, b) => a.title.localeCompare(b.title));
    });

    // Create brands list UI
    const listHTML = Object.entries(brandsByLetter)
      .sort(([a], [b]) => a === '#' ? -1 : b === '#' ? 1 : a.localeCompare(b))
      .map(([letter, brands]) => `
        <div class="mobile-navigation__brands-section" data-section="${letter}">
          <h3 class="mobile-navigation__brands-heading">${letter}</h3>
          <ul class="mobile-navigation__brands-items">
            ${brands.map(brand => `
              <li class="mobile-navigation__brands-item">
                <a href="${brand.url}" class="mobile-navigation__brands-link">${brand.title}</a>
                ${brand.badgeText ? `<span 
                style="background-color: ${brand.badgeBackgroundColour || '#000'}; 
                color: ${brand.badgeTextColour || '#fff'};"
                class="badge main-nav__badge">
                ${brand.badgeText || ''}
              </span>` : ''}
              </li>
            `).join('')}
          </ul>
        </div>
      `).join('');
    listContainer.innerHTML = listHTML;

    // Add click handlers for alphabet buttons
    alphabetContainer.addEventListener('click', (evt) => {
      const letter = evt.target.dataset.letter;
      if (!letter || evt.target.classList.contains('is-disabled')) return;

      const section = listContainer.querySelector(`[data-section="${letter}"]`);
      if (section) {
        const brandsContainer = brandsMenu.querySelector('.mobile-navigation__brands');
        if (brandsContainer) {
          const scrollPosition = section.offsetTop - 20; // Subtract some padding
          brandsContainer.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        }
      }
    });
  }
}

customElements.define('mobile-navigation', MobileNavigation);
